import React, { useEffect, useRef, useState } from 'react';
import {
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { notify } from '../../services/dialog';
import LogoMark from '../LogoMark';
import * as auth from '../../services/auth';
import type { Session as AuthSession } from '../../services/auth';
import * as referrals from '../../services/referrals';
import { ApiError } from '../../services/types';
import { colors, inputText, radii, screenPadding, spacing } from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * Phone-and-OTP sign-in, as a sheet rather than a screen.
 *
 * The app is browsable signed-out by design, so sign-in is not a gate at the
 * front door - it is asked for at the first moment it is actually needed, when
 * someone opens or adds a service. Rising over Home keeps that context: the
 * catalogue stays visible behind the scrim, and dismissing returns to exactly
 * the same scroll position rather than to a fresh screen.
 *
 * Centred layout with the brand mark on a Forest tile at the top. The mark
 * rather than a padlock on purpose - a lock frames signing in as a barrier,
 * which is the opposite of what this moment is meant to feel like.
 *
 * ---------------------------------------------------------------------------
 * THIS IS REAL NOW
 * ---------------------------------------------------------------------------
 * It calls `POST /auth/otp` and `POST /auth/verify`. A code is generated,
 * hashed and stored server-side; the account is created the first time one is
 * accepted. Every rule that makes a four-digit secret safe - expiry, an
 * attempt cap, single use, a resend cooldown - is enforced on the server, not
 * here. The countdown below is a courtesy so the button looks right; the
 * server refuses early resends regardless of what this component believes.
 *
 * WHAT THE SHEET IS RESPONSIBLE FOR
 *   - showing failures in a place the customer is already looking
 *   - not letting a second tap fire a second request
 *   - clearing the code on a rejection, because the next attempt is a new one
 *
 * NO SMS IS SENT YET. The backend runs `ConsoleOtpSender`, which logs the code
 * to the server terminal and returns it as `devCode` - which this sheet shows
 * as a dev hint. Both disappear the moment a real provider is configured, and
 * the backend refuses to boot in production without one.
 */

/**
 * How many digits the code has.
 *
 * MUST MATCH `OTP_LENGTH` IN THE BACKEND'S `.env`. It is not negotiable and it
 * is not cosmetic: this number is the `maxLength` on the hidden input, the
 * `slice` that truncates what you type, and the test that enables Verify. Set
 * it to 4 against a server issuing 6 and the field silently drops the last two
 * digits - you can never enter the code you were sent, and every attempt comes
 * back as "incorrect", which reads as a server bug rather than a UI one.
 *
 * The server does not announce the length in `/auth/otp`, so there is nothing
 * to derive this from. If it ever does, read it from the response instead of
 * this constant.
 */
const CODE_LENGTH = 6;
const PHONE_LENGTH = 10;
const RESEND_SECONDS = 30;

/**
 * Ten digits as they are read aloud - "78988 17127".
 *
 * Display only. `phone` in state stays bare digits, because that is what the
 * server takes and what `PHONE_LENGTH` counts; the space is put in on the way
 * to the screen and stripped again in `onChangeText`.
 */
const formatPhoneInput = (digits: string): string =>
  digits.length > 5 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;

/**
 * Whatever arrived in the field, reduced to the ten digits that matter.
 *
 * Autofill is the reason this is more than `replace(/\D/g, '')`. iOS offers
 * the number formatted - "+91 78988 17127" - and the old `maxLength={10}` on
 * the input truncated that STRING to ten characters before this handler ever
 * ran, so accepting the suggestion filled in nine digits and the field looked
 * broken. The length cap lives here now, applied to digits rather than to
 * characters, and the country code is peeled off rather than counted.
 */
const digitsFromInput = (raw: string): string => {
  let digits = raw.replace(/\D/g, '');
  // "+91 78988 17127" → 917898817127. The prefix is the country, not the number.
  if (digits.length > PHONE_LENGTH && digits.startsWith('91')) {
    digits = digits.slice(2);
  }
  // "078988 17127" - a leading trunk zero some contact cards carry.
  if (digits.length > PHONE_LENGTH && digits.startsWith('0')) {
    digits = digits.replace(/^0+/, '');
  }
  return digits.slice(0, PHONE_LENGTH);
};

type Stage = 'phone' | 'code';

type Props = {
  visible: boolean;
  onClose: () => void;
  /**
   * The verified session - tokens and the account behind them.
   *
   * The whole session rather than just the number: the caller has to store the
   * tokens, and handing back a phone string would mean it had to go and fetch
   * what the server has already sent.
   */
  onVerified: (session: AuthSession) => void;
};

function AuthSheet({ visible, onClose, onVerified }: Props) {
  const [stage, setStage] = useState<Stage>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  /**
   * A friend's code, typed in before the account even exists.
   *
   * Optional and free-text at this point - it is not checked against the
   * server until `verify()` below, because `POST /customers/me/referrals/
   * apply` needs a bearer token and there is nobody signed in yet to hold
   * one. A wrong or already-used code is not a reason to refuse the sign-in
   * that was actually asked for, so it can only fail quietly - see `verify`.
   */
  const [referralCode, setReferralCode] = useState('');
  /** Whether the field itself is showing yet. See `styles.field` below it. */
  const [referralOpen, setReferralOpen] = useState(false);
  const referralInput = useRef<TextInput>(null);
  const [busy, setBusy] = useState(false);
  const [countdown, setCountdown] = useState(0);
  /** The last failure, shown under the field it belongs to. */
  const [error, setError] = useState<string | null>(null);
  /** The code, when the backend is running without an SMS provider. */
  const [devCode, setDevCode] = useState<string | null>(null);
  /** Ties a submitted code back to the request that issued it. Required on verify. */
  const [providerRef, setProviderRef] = useState<string | null>(null);

  const codeInput = useRef<TextInput>(null);
  /** False once the sheet unmounts, so a landed request cannot set state. */
  const alive = useRef(true);

  const phoneReady = phone.length === PHONE_LENGTH;
  const codeReady = code.length === CODE_LENGTH;

  // Reset whenever the sheet is dismissed, so it never reopens mid-flow with a
  // stale number, a spent code, or last time's error still on screen.
  useEffect(() => {
    if (visible) {
      return;
    }
    setStage('phone');
    setPhone('');
    setCode('');
    setReferralCode('');
    setReferralOpen(false);
    setBusy(false);
    setCountdown(0);
    setError(null);
    setDevCode(null);
    setProviderRef(null);
  }, [visible]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }
    const tick = setTimeout(() => setCountdown(n => n - 1), 1000);
    return () => clearTimeout(tick);
  }, [countdown]);

  /**
   * Ask for a code.
   *
   * Also the Resend handler - resending IS requesting, and having the button
   * only restart the local countdown was the stub's one real lie: it looked
   * like it had done something and no second message ever came.
   */
  const requestCode = async () => {
    if (!phoneReady || busy) {
      return;
    }
    Keyboard.dismiss();
    setBusy(true);
    setError(null);

    try {
      const result = await auth.requestOtp(phone);
      if (!alive.current) {
        return;
      }

      setStage('code');
      // The server's own cooldown, not a constant here - the two must agree,
      // and only one of them is enforced.
      setCountdown(result.resendInSeconds || RESEND_SECONDS);
      setDevCode(result.devCode ?? null);
      setProviderRef(result.providerRef);
      setCode('');
      codeInput.current?.focus();
    } catch (thrown) {
      if (!alive.current) {
        return;
      }
      setError(messageFrom(thrown));
    } finally {
      if (alive.current) {
        setBusy(false);
      }
    }
  };

  const verify = async () => {
    if (!codeReady || busy || !providerRef) {
      return;
    }
    Keyboard.dismiss();
    setBusy(true);
    setError(null);

    try {
      const session = await auth.verifyOtp(phone, code, providerRef);
      if (!alive.current) {
        return;
      }

      /*
       * Best-effort, and only for a brand-new account.
       *
       * `verifyOtp` already set the bearer token, so the call can go out -
       * but a bad or already-claimed code is the ONE failure here that must
       * not undo the sign-in that just succeeded. The account exists either
       * way; the reward is a bonus this attempt either lands or does not. So
       * it stays unawaited: `onVerified` runs immediately below, and putting
       * a network round trip in front of it would hold the sheet open on a
       * bad connection for a reward.
       *
       * NOT SILENT ANY MORE, THOUGH.
       *
       * The failure used to be swallowed whole. Someone mistyped a friend's
       * code, or used one that was blocked or capped, and nothing happened:
       * no message, no reward, and - until Refer & Earn grew an entry field -
       * no second chance anywhere in the app. "Referrals don't work" was
       * mostly this.
       *
       * A dialog rather than `setError`: by the time this resolves the sheet
       * has been closed by `onVerified`, so there is no component left to
       * render into. A native alert does not care, and it names the way back
       * in - the entry card on Refer & Earn takes the same code right up
       * until the first booking completes.
       */
      const trimmedReferral = referralCode.trim();
      if (session.isNewUser && trimmedReferral) {
        referrals.apply(trimmedReferral).catch((thrown: unknown) => {
          notify(
            'Referral code not applied',
            `${messageFrom(
              thrown,
            )}\n\nYou are signed in. Add a code from Account → Refer & Earn - it still counts until your first booking is done.`,
          );
        });
      }

      onVerified(session);
    } catch (thrown) {
      if (!alive.current) {
        return;
      }
      setError(messageFrom(thrown));
      // Cleared, and focus returned. A rejected code left in the boxes means
      // the next tap of Verify resubmits the same wrong digits and burns
      // another of the five attempts for nothing.
      setCode('');
      codeInput.current?.focus();
    } finally {
      if (alive.current) {
        setBusy(false);
      }
    }
  };

  return (
    <BottomSheet visible={visible} onClose={onClose}>
      {/*
        Scrolls only when it has to.

        `flexGrow: 1` keeps the form laid out exactly as before whenever there
        is room - which is every normal phone. On a short one with the keyboard
        up, the sheet is capped to what the keys leave (see `BottomSheet`) and
        the content is taller than that; without a scroller the last control
        would simply be cut off the bottom.
      */}
      <ScrollView
        contentContainerStyle={styles.body}
        showsVerticalScrollIndicator={false}
        /* A tap aimed at a button inside must not be eaten by the keyboard
           dismissing. */
        keyboardShouldPersistTaps="handled"
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          hitSlop={10}
          style={({ pressed }) => [
            styles.close,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon
            name="plus"
            size={16}
            color={colors.inkMuted}
            strokeWidth={2.2}
          />
        </Pressable>

        <View style={styles.badge}>
          <LogoMark size={34} color={colors.onPrimary} />
        </View>

        {stage === 'phone' ? (
          <>
            <Text variant="title" align="center">
              Let's get started
            </Text>
            <Text
              variant="bodySm"
              color={colors.inkMuted}
              align="center"
              style={styles.blurb}
            >
              Sign in to continue and book{'\n'}your service
            </Text>

            <View style={styles.field}>
              <Text variant="label" color={colors.inkBody}>
                +91
              </Text>
              <View style={styles.divider} />
              <TextInput
                /* Shown with the reading space; held as bare digits. */
                value={formatPhoneInput(phone)}
                onChangeText={next => setPhone(digitsFromInput(next))}
                placeholder="Mobile number"
                placeholderTextColor={colors.inkSubtle}
                keyboardType="number-pad"
                textContentType="telephoneNumber"
                autoComplete="tel"
                autoFocus
                /*
                 * No `maxLength`. It counted CHARACTERS, before sanitising -
                 * so an autofilled "+91 78988 17127" was cut to ten characters
                 * and lost its last digits. `digitsFromInput` caps the digits
                 * instead, after stripping spaces and the country code.
                 */
                style={styles.input}
                accessibilityLabel="Mobile number"
                onSubmitEditing={requestCode}
              />
            </View>

            {/*
              Collapsed behind a link until asked for, the same way Resend is
              text rather than a standing button - a second box on a one-field
              form outweighs what is actually optional. Tapping it swaps the
              link for the input and focuses it in one motion, so the tap
              reads as opening the field rather than as two separate actions.
            */}
            {referralOpen ? (
              <View style={styles.field}>
                <TextInput
                  ref={referralInput}
                  value={referralCode}
                  onChangeText={next => setReferralCode(next.toUpperCase())}
                  placeholder="Referral code"
                  placeholderTextColor={colors.inkSubtle}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  style={styles.input}
                  accessibilityLabel="Referral code, optional"
                  onSubmitEditing={requestCode}
                />
              </View>
            ) : (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Add a referral code"
                onPress={() => {
                  setReferralOpen(true);
                  requestAnimationFrame(() => referralInput.current?.focus());
                }}
                hitSlop={8}
                style={styles.referralLink}
              >
                <Text variant="caption" color={colors.primary}>
                  Have a referral code?
                </Text>
              </Pressable>
            )}

            {/* Under the field it belongs to, not in an alert. A dialog takes
                the keyboard away and puts the failure somewhere the eye is
                not, which on a one-field form is the wrong side of the
                screen. */}
            <ErrorLine message={error} />

            <PrimaryButton
              label={busy ? 'Sending…' : 'Send code'}
              disabled={!phoneReady || busy}
              onPress={requestCode}
            />

            <Text
              variant="caption"
              color={colors.inkMuted}
              align="center"
              style={styles.helper}
            >
              We'll send you a {CODE_LENGTH}-digit OTP
            </Text>
          </>
        ) : (
          <>
            <Text variant="title" align="center">
              Enter the code
            </Text>
            <Text
              variant="bodySm"
              color={colors.inkMuted}
              align="center"
              style={styles.blurb}
            >
              Sent to +91 {phone}
            </Text>

            {/*
              One field behind six boxes, rather than six inputs. Six would
              each need focus-forwarding and backspace handling, and an
              autofilled code would only reach the first.

              The field is FULL SIZE and COVERED - see `codeInput`.
            */}
            <View style={styles.codeField}>
              {/*
                The input is rendered FIRST so it sits underneath, and the
                boxes are painted over it. See `codeInput` for why it is
                hidden by being covered rather than by being made transparent.
              */}
              <TextInput
                ref={codeInput}
                value={code}
                onChangeText={next =>
                  setCode(next.replace(/\D/g, '').slice(0, CODE_LENGTH))
                }
                keyboardType="number-pad"
                /* iOS: offers the code from Messages in the QuickType bar. */
                textContentType="oneTimeCode"
                /* Android: the autofill hint for an SMS one-time code. */
                autoComplete="sms-otp"
                importantForAutofill="yes"
                maxLength={CODE_LENGTH}
                style={styles.codeInput}
                autoFocus
                caretHidden
              />

              <Pressable
                style={styles.boxes}
                onPress={() => codeInput.current?.focus()}
                accessibilityRole="button"
                accessibilityLabel="Verification code"
              >
                {Array.from({ length: CODE_LENGTH }, (_, i) => (
                  <View
                    key={i}
                    style={[
                      styles.box,
                      i === code.length ? styles.boxActive : null,
                    ]}
                  >
                    <Text variant="title">{code[i] ?? ''}</Text>
                  </View>
                ))}
              </Pressable>
            </View>

            {/*
              The code, when no SMS provider is wired up.

              Marked as a development aid rather than dressed up as a hint,
              because it IS the secret sitting on the screen. The backend only
              sends `devCode` outside production and refuses to boot there
              without a real sender, so this cannot survive to a release.
            */}
            {devCode ? (
              <View style={styles.devCode}>
                <Text variant="eyebrow" color={colors.inkMuted}>
                  DEV - no SMS sent
                </Text>
                <Text variant="title" color={colors.primary}>
                  {devCode}
                </Text>
              </View>
            ) : null}

            <ErrorLine message={error} />

            <PrimaryButton
              label={busy ? 'Verifying…' : 'Verify'}
              disabled={!codeReady || busy}
              onPress={verify}
            />

            <View style={styles.codeFooter}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Change number"
                onPress={() => {
                  setStage('phone');
                  setCode('');
                }}
                hitSlop={8}
              >
                <Text variant="caption" color={colors.primary}>
                  Change number
                </Text>
              </Pressable>

              {/* Actually resends. The stub only restarted its own
                  countdown, so the button looked like it worked and no
                  second message ever came. */}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Resend code"
                disabled={countdown > 0 || busy}
                onPress={requestCode}
                hitSlop={8}
              >
                <Text
                  variant="caption"
                  color={
                    countdown > 0 || busy ? colors.inkSubtle : colors.primary
                  }
                >
                  {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
                </Text>
              </Pressable>
            </View>
          </>
        )}
      </ScrollView>
    </BottomSheet>
  );
}

/**
 * One line of failure, in the flow rather than over it.
 *
 * Renders nothing when there is nothing wrong - rather than reserving the
 * space - because a permanent gap under the field reads as a missing label,
 * and the sheet grows by one line when it appears, which is itself the signal
 * that something changed.
 */
function ErrorLine({ message }: { message: string | null }) {
  if (!message) {
    return null;
  }

  return (
    <View style={styles.error} accessibilityRole="alert">
      <Icon name="xCircle" size={14} color={colors.danger} />
      <Text variant="caption" color={colors.danger} style={styles.errorText}>
        {message}
      </Text>
    </View>
  );
}

/**
 * What to put on screen for a thrown failure.
 *
 * `ApiError.message` is always safe to show: the server writes its messages
 * for a customer to read, and the client writes the network ones the same way.
 * Anything else is a bug in this app, and its message would be a stack-trace
 * fragment - so it gets a generic line instead.
 */
function messageFrom(thrown: unknown): string {
  if (thrown instanceof ApiError) {
    return thrown.message;
  }
  return 'Something went wrong. Please try again.';
}

function PrimaryButton({
  label,
  disabled,
  onPress,
}: {
  label: string;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.primary,
        disabled ? styles.primaryOff : null,
        pressed && !disabled ? styles.pressed : null,
      ]}
    >
      <Text variant="label" color={colors.onPrimary}>
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  /*
   * The steps down the sheet, in order: 16 under the badge, 4 under the
   * title, 16 to the field, 16 to the button, 8 to the helper, 8 to the
   * bottom. One step per relationship, tightening as the block goes on, so
   * the mark and the heading read as one unit and the form as another.
   *
   * `paddingBottom` is small on purpose - `BottomSheet` adds the safe-area
   * inset under this, and a second helping of air here is what left a band of
   * empty sheet above the keyboard.
   */
  body: {
    /* Fills the sheet when the content is shorter than it, so the layout is
       unchanged in the normal case; scrolls when it is taller. */
    flexGrow: 1,
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
  },
  /*
   * Absolute, so it does not push the centred badge off-axis - a close button
   * in the flow would need a matching spacer on the left to keep the mark in
   * the middle.
   */
  close: {
    position: 'absolute',
    top: spacing.md,
    right: screenPadding,
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
    // A rotated plus - the icon set has no cross glyph.
    transform: [{ rotate: '45deg' }],
    zIndex: 1,
  },
  /** The app icon, restated: Forest tile, ivory mark, on the app's own corner. */
  badge: {
    width: 62,
    height: 62,
    borderRadius: radii.xl,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    marginBottom: spacing.lg,
  },
  blurb: {
    marginTop: spacing.xs,
  },

  field: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: spacing.md,
    height: 54,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.lg,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceMuted,
  },
  /* Same top offset as `field` above, so the link sits where the box it
     replaces would have - swapping one for the other does not shift
     anything below it by more than a line's difference in height. */
  referralLink: {
    alignSelf: 'center',
    marginTop: spacing.lg,
    paddingVertical: spacing.xs,
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: colors.borderStrong,
  },
  input: {
    flex: 1,
    ...inputText,
  },

  /*
   * Centred at its own size, not stretched across the sheet.
   *
   * `alignSelf: 'stretch'` with `flex: 1` boxes divided the full width between
   * four cells, so each one was wider than the digit it holds and the row read
   * as four empty fields rather than as one code. Fixed cells keep the group
   * compact and the same size whatever the screen or the code length.
   */
  /*
   * Sized for six boxes, not four.
   *
   * At the old 52pt wide with an 8pt gap, six of these need 352pt. A 360pt
   * phone has about 336 once the sheet's own gutters are off, so the last box
   * was pushed off the edge - and it is the one you are looking at when you
   * finish typing. 44pt with a 4pt gap needs 284, which clears even a 320pt
   * screen.
   *
   * If `CODE_LENGTH` changes again, check this arithmetic before shipping it.
   */
  boxes: {
    flexDirection: 'row',
    alignSelf: 'center',
    gap: spacing.xs,
    marginTop: spacing.lg,
    /*
     * Opaque, because it is what hides the input beneath it.
     *
     * The boxes themselves are already filled, but the gaps between them are
     * not, and anything the platform paints on the field would show through.
     */
    backgroundColor: colors.background,
  },
  box: {
    width: 44,
    height: 52,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceMuted,
  },
  boxActive: {
    borderColor: colors.primary,
    backgroundColor: colors.surface,
  },
  /* Holds the boxes and the real input in the same frame. */
  codeField: {
    position: 'relative',
  },
  /*
   * Full size, focusable, and hidden by being COVERED.
   *
   * ---------------------------------------------------------------------------
   * WHY NOT JUST MAKE IT INVISIBLE
   * ---------------------------------------------------------------------------
   * Two obvious approaches both fail, in opposite directions:
   *
   *   `opacity: 0`          Android only offers autofill to a view that passes
   *                         `isVisibleToUser()`, and zero alpha fails it. The
   *                         code stopped being offered at all.
   *   `color: transparent`  Not honoured once the field is AUTOFILLED. The
   *                         platform repaints a just-filled field itself, so
   *                         the raw code appeared across the top-left box.
   *
   * So the field is left entirely alone - full size, full opacity, ordinary
   * text colour - and the boxes are drawn OVER it, opaque. Occlusion needs no
   * cooperation from the platform: whatever Android chooses to paint, it is
   * painted underneath something solid. `isVisibleToUser()` cares about alpha
   * and visibility flags, not about what happens to overlap the view, so
   * autofill is unaffected.
   *
   * The size and position are what autofill anchors its dropdown to, which is
   * why this fills the row rather than hiding in a corner. Touches land on the
   * boxes above and are forwarded here with `focus()`.
   */
  codeInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },

  primary: {
    alignSelf: 'stretch',
    height: 52,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.lg,
    borderRadius: radii.md,
    backgroundColor: colors.primary,
  },
  primaryOff: {
    backgroundColor: colors.borderStrong,
  },
  helper: {
    marginTop: spacing.sm,
  },
  error: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    gap: spacing.xs,
    marginTop: spacing.md,
  },
  errorText: {
    flex: 1,
  },
  /* Dashed, so it reads as scaffolding rather than as part of the design -
     the same treatment the stand-in gateway's outcome picker gets. */
  devCode: {
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: 2,
    marginTop: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.borderStrong,
  },
  codeFooter: {
    flexDirection: 'row',
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    marginTop: spacing.lg,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default AuthSheet;
